import { Col, Row, Switch } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { OptionModal } from '..';
import { Option, OptionCategoryDto, UpdateOptionDto } from '../../../../apis/client-axios';
import ButtonWrap from '../../../../components/ButtonWrap';
import SvgAdd from '../../../../components/svg/SvgAdd';
import SvgDelete from '../../../../components/svg/SvgDelete';
import SvgEdit from '../../../../components/svg/SvgEdit';
import TableWrap from '../../../../components/TableWrap';

interface CategoryProps {
  loading: boolean;
  category: OptionCategoryDto;
  options: Option[];
  onUpdateOption: (payload: { id: string; updateOptionDto: UpdateOptionDto }) => void;
  onOpenModal: (payload: OptionModal) => void;
}

const n = (key: keyof Option) => key;

const Category: FC<CategoryProps> = (props) => {
  const { loading, category, options, onUpdateOption, onOpenModal } = props;

  const intl = useIntl();

  const columns: ColumnsType<Option> = [
    {
      title: intl.formatMessage({ id: 'choiceSettings.choice' }),
      dataIndex: n('title'),
      key: n('title'),
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'choiceSettings.availability' }),
      width: '8%',
      dataIndex: n('status'),
      key: n('status'),
      align: 'center',
      render: (status: boolean, option) => {
        const {
          isAnnualLeaveOption,
          isCompensatoryLeaveOption,
          isMakeUpWorkOption,
          isWorkOnDayOffOption,
          isDefaultOption,
        } = option;
        const disabled =
          isAnnualLeaveOption ||
          isCompensatoryLeaveOption ||
          isMakeUpWorkOption ||
          isWorkOnDayOffOption ||
          isDefaultOption;

        return (
          <Switch
            checked={status}
            onChange={(checked) => onUpdateOption({ id: option.id, updateOptionDto: { status: checked } })}
            disabled={disabled}
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'common.inputDisplay' }),
      width: '8%',
      dataIndex: n('isRequireAdditionalDetail'),
      key: n('isRequireAdditionalDetail'),
      align: 'center',
      render: (isRequireAdditionalDetail: boolean, option) => {
        return (
          <Switch
            checked={isRequireAdditionalDetail}
            onChange={(checked) =>
              onUpdateOption({ id: option.id, updateOptionDto: { isRequireAdditionalDetail: checked } })
            }
          />
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'common.sendLineMessage' }),
      width: '8%',
      dataIndex: n('isSendLineMessage'),
      key: n('isSendLineMessage'),
      align: 'center',
      render: (isSendLineMessage: boolean, option) => {
        return (
          <Switch
            checked={isSendLineMessage}
            onChange={(checked) => onUpdateOption({ id: option.id, updateOptionDto: { isSendLineMessage: checked } })}
          />
        );
      },
    },
    {
      title: '',
      width: '15%',
      key: 'action',
      render: (_, option) => {
        const {
          isAnnualLeaveOption,
          isCompensatoryLeaveOption,
          isMakeUpWorkOption,
          isWorkOnDayOffOption,
          isDefaultOption,
        } = option;
        const isShowButton =
          !isAnnualLeaveOption &&
          !isCompensatoryLeaveOption &&
          !isMakeUpWorkOption &&
          !isWorkOnDayOffOption &&
          !isDefaultOption;

        return (
          isShowButton && (
            <Row gutter={[12, 12]} align="middle" justify="center">
              <Col>
                <ButtonWrap
                  variant="filled"
                  color="gray"
                  width={78}
                  height={32}
                  onClick={() => onOpenModal({ type: 'upsert', option })}
                >
                  <SvgEdit /> {intl.formatMessage({ id: 'common.edit' })}
                </ButtonWrap>
              </Col>

              <Col>
                <ButtonWrap
                  color="gray"
                  width={101}
                  height={32}
                  className="options-setting-item-remove"
                  onClick={() => onOpenModal({ type: 'delete', option })}
                >
                  <SvgDelete />
                  {intl.formatMessage({ id: 'common.remove' })}
                </ButtonWrap>
              </Col>
            </Row>
          )
        );
      },
    },
  ];

  return (
    <>
      <TableWrap
        loading={loading}
        tableProps={{
          dataSource: options,
          columns: columns as ColumnsType,
        }}
        leftTopContent={<span className="options-setting-item-category">{category.title}</span>}
        rightTopContent={
          <ButtonWrap
            width={124}
            height={48}
            onClick={() => onOpenModal({ type: 'upsert', option: { category: category.code } as Option })}
          >
            <SvgAdd /> {intl.formatMessage({ id: 'choiceSettings.add' })}
          </ButtonWrap>
        }
      />
    </>
  );
};

export default Category;
